<template>
  <div
    class="tir-pm-comment-chat-message-line"
    :class="{
      'tir-pm-comment-chat-message-line--owner-message': props.isOwnerMessage,
      'tir-pm-comment-chat-message-line--not-owner-message': !props.isOwnerMessage,
    }"
  >
    <div
      class="tir-pm-comment-chat-message"
      :data-comment-id="commentData.Id"
      :class="{
        'tir-pm-comment-chat-message--owner-message': props.isOwnerMessage,
        'tir-pm-comment-chat-message--not-owner-message': !props.isOwnerMessage,
      }"
    >
      <div class="tir-pm-comment-chat-message__top-part">
        <div class="tir-pm-comment-chat-message__user-info">
          <div class="tir-pm-comment-chat-message__inner-user-photo">
            <TirPmTooltipElement>
              <TirPmIconView
                :size="24"
                isRounded
                :colorCode="computedCommentData.Author.AvatarColor"
                :imageLoadUrl="computedCommentData.Author.ImageUrl"
                :is-bordered="false"
              >
                <template #mainSlot>
                  {{ computedCommentData.Author?.FirstName[0].toUpperCase()
                  }}{{ computedCommentData.Author?.LastName[0].toUpperCase() }}
                </template>
              </TirPmIconView>
              <template #content>
                <span class="tir-pm-comment-chat-message__author-info"
                  >{{ computedCommentData.Author.FirstName }}
                  {{ computedCommentData.Author.MiddleName }}
                  {{ computedCommentData.Author.LastName }}</span
                >
              </template>
            </TirPmTooltipElement>
          </div>
          <div class="tir-pm-comment-chat-message__user-name">
            {{ computedCommentData.Author?.FirstName }}
          </div>
        </div>
      </div>
      <div class="tir-pm-comment-chat-message__quote-list" v-if="computedCommentData?.ReplyComment">
        <TirPmCommentChatQuoteMessage
          :commentData="computedCommentData?.ReplyComment"
          :isClickable="true"
          @toReply="onClickReply"
        ></TirPmCommentChatQuoteMessage>
      </div>
      <div
        class="tir-pm-comment-chat-message__message"
        v-for="comment in commentsList"
        :key="comment.Value"
      >
        <span v-if="comment.Type === CommentContentTypeEnum.Text">
          {{ comment.Value }}
        </span>
        <a
          v-else-if="comment.Type === CommentContentTypeEnum.Link"
          :href="comment.Value"
          target="_blank"
        >
          {{ comment.Value }}
        </a>
      </div>
      <div class="tir-pm-comment-chat-message__file-addon" v-if="!!computedFilePreview">
        <TirPmFilePreview :file="computedFilePreview" hideControls></TirPmFilePreview>
      </div>

      <div class="tir-pm-comment-chat-message__bottom-part">
        <div class="tir-pm-comment-chat-message__time">
          {{ timeSince(computedCommentData.DateCreate) }}
        </div>
        <TirPmPopover
          :position="computedPositionOption"
          width="11.688rem"
          :disableContentBlur="true"
          ref="$popoverOptions"
        >
          <template #default="{ isOpened }">
            <div
              class="tir-pm-comment-chat-message-options-button"
              :class="{ 'tir-pm-comment-chat-message-options-button--active': isOpened }"
            >
              <EllipsisHorizontalIcon></EllipsisHorizontalIcon>
            </div>
          </template>
          <template #content>
            <div class="tir-pm-comment-chat-message-options">
              <div class="tir-pm-comment-chat-message-options__item" @click="onReplyMessage">
                <ArrowUturnRightIcon />
                Ответить
              </div>
              <div
                class="tir-pm-comment-chat-message-options__item"
                v-if="props.isOwnerMessage"
                @click="onUpdateMessage"
              >
                <PencilSquareIcon />
                Отредактировать
              </div>
              <div
                class="tir-pm-comment-chat-message-options__item"
                v-if="props.isOwnerMessage"
                @click="onTrashMessage"
              >
                <TrashIcon />
                Удалить
              </div>
            </div>
          </template>
        </TirPmPopover>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineProps, PropType, ref } from 'vue';
import { CurrentUser } from 'tir-pm-user-core';
import {
  PopoverPositionEnum,
  TirPmIconView,
  TirPmPopover,
  TirPmFilePreview,
  IFilePreview,
  TirPmTooltipElement,
} from 'tir-components';
import {
  ArrowUturnRightIcon,
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  TrashIcon,
} from 'tir-style-system/icons/outline';
import { CommentChatMessageModel, CommentContentModel } from './models';
import TirPmCommentChatQuoteMessage from './TirPmCommentChatQuoteMessage.vue';
import { CommentContentTypeEnum } from './enums';

const $popoverOptions = ref<typeof TirPmPopover>(null);
/**
 * События
 */

const emit = defineEmits<{
  (eventName: 'onTrash', value: CommentChatMessageModel): void;
  (eventName: 'onReply', value: CommentChatMessageModel): void;
  (eventName: 'clickReply', value: CommentChatMessageModel): void;
  (eventName: 'onUpdate', value: CommentChatMessageModel): void;
}>();

/**Параметры компонента */
const props = defineProps({
  isOwnerMessage: { type: Boolean },
  /**Данные комментария */
  commentData: { type: Object as PropType<CommentChatMessageModel>, required: true },
});

/**
 * Отправить событие на ответ
 */
const onReplyMessage = () => {
  $popoverOptions.value.close();
  emit('onReply', computedCommentData.value);
};

/**
 * Обновить комментарий
 */
const onUpdateMessage = () => {
  $popoverOptions.value.close();
  emit('onUpdate', computedCommentData.value);
};

/**
 * Удалить сообщение
 */
const onTrashMessage = () => {
  $popoverOptions.value.close();
  emit('onTrash', computedCommentData.value);
};

const onClickReply = (replyComment) => {
  emit('clickReply', replyComment);
};
/**
 * Данные
 */
const computedCommentData = computed(() => props.commentData);
/**Комментарии */
const commentsList = computed<CommentContentModel[]>(() => {
  const message = props.commentData.Message || '';
  const urlPattern = /(https?:\/\/[^\s]+)/g;

  return message.split(urlPattern).map((part) => ({
    Type: urlPattern.test(part) ? CommentContentTypeEnum.Link : CommentContentTypeEnum.Text,
    Value: part,
  }));
});

const computedFilePreview = computed<IFilePreview>(() => {
  if (computedCommentData.value?.File) {
    return {
      FileId: computedCommentData.value.File.Id,
      Url: computedCommentData.value.File.Url,
      Name: computedCommentData.value.File.Name,
      Size: computedCommentData.value.File.Size,
      VideoPreviewUrl: computedCommentData.value.File.ThumbnailUrl,
      Accesses: [],
      IsProccessing: computedCommentData.value.File.IsProccessing,
      Progress: computedCommentData.value.File.IsProccessing ? 100 : undefined,
      User: {
        Id: computedCommentData.value.Author.Id,
        FirstName: computedCommentData.value.Author.FirstName,
        LastName: computedCommentData.value.Author.LastName,
        MiddleName: computedCommentData.value.Author.MiddleName,
        AvatarColor: computedCommentData.value.Author.AvatarColor,
        Image: computedCommentData.value.Author.ImageUrl,
      },
    };
  } else {
    return null;
  }
});

/**
 * Вычислить изначальную позицию опций при нажатии на кнопку
 */
const computedPositionOption = computed(() =>
  computedCommentData.value.Author.Id == CurrentUser.Id
    ? PopoverPositionEnum.Left
    : PopoverPositionEnum.Right,
);

const timeSince = (date: Date): string => {
  const timezone = new Date().getTimezoneOffset() / 60;
  date = new Date(date);
  date.setHours(date.getHours() - timezone);

  const elapsed = Math.floor((Date.now() - date.getTime()) / 1000);
  const oneDay = 86400; // 24 * 60 * 60

  if (elapsed >= oneDay) {
    return date.toLocaleTimeString('ru-RU', {
      hour: 'numeric',
      minute: 'numeric',
    });
  }

  const periods: { time: number; name: string }[] = [
    { time: 3600, name: 'час.' },
    { time: 60, name: 'мин.' },
    { time: 1, name: 'сек.' },
  ];

  for (const { time, name } of periods) {
    if (elapsed >= time) {
      return `${Math.floor(elapsed / time)} ${name} назад`;
    }
  }

  return 'только что';
};
</script>

<style lang="scss">
@import './style/tir-pm-comment-chat-message';
</style>
